<template>
  <div class="campaign-list-item" :class="{ removing: removingCampaign }">
    <div class="campaign-details">
      <CampaignListStatusIcon :key="reloading" :status="status" />

      <div>
        <CampaignListSubject
          :id="campaign.campaignId"
          :status="campaign.status"
          :subject="campaign.subject"
          :preview-url="campaign.previewUrl"
        />

        <div class="campaign-meta">
          <CampaignListStatus :status="campaign.status" class="mr-2" />
          <CampaignListDate v-if="dateSent" label="Sent" :date="dateSent" />
          <CampaignListDate
            v-else
            label="Created"
            :date="campaign.dateCreated"
          />
        </div>

        <CreateCampaignLogic v-if="dateSent" :campaign-id="campaign.campaignId">
          <Button
            slot-scope="{ createCampaign, creatingCampaign }"
            class="link p-0 resend-campaign"
            :loading="creatingCampaign"
            @click="createCampaign"
            >Create new
            <span class="resend-campaign-help"
              >(with this template)</span
            ></Button
          >
        </CreateCampaignLogic>

        <Button
          v-if="campaign.status === 'SENDING'"
          class="link status-reload"
          :disabled="reloading"
          @click="$emit('refetch-campaigns')"
        >
          ({{ reloadLabel }})
        </Button>
      </div>
    </div>

    <div
      v-if="campaign.status === 'DOWNLOADED' || campaign.status === 'DRAFT'"
      class="campaign-actions"
    >
      <div class="send-button-wrap">
        <Button
          class="secondary send-button"
          :to="`/campaign/${campaign.campaignId}?approveSend=true`"
          :disabled="removingCampaign"
        >
          <v-icon
            slot="icon-start"
            name="paper-plane"
            scale="0.8"
            class="mr-1"
          />
          Send Newsletter
        </Button>
      </div>

      <div class="meta-buttons-wrap">
        <Button
          v-if="campaign.status === 'DRAFT'"
          class="link meta-button delete-button"
          :disabled="removingCampaign"
          @click="onRemoveCampaign"
        >
          <v-icon slot="icon-start" name="times" class="mr-1" /> Delete
        </Button>

        <Button
          class="link meta-button"
          :to="`/campaign/${campaign.campaignId}`"
          :disabled="removingCampaign"
        >
          <v-icon
            slot="icon-start"
            name="pencil-alt"
            class="mr-1"
            scale="0.8"
          />
          Edit
        </Button>
      </div>
    </div>

    <CampaignListStats v-else :campaign="campaign" />
  </div>
</template>

<script>
import CampaignListSubject from "@/components/Campaigns/CampaignList/CampaignListSubject";
import CampaignListStatus from "@/components/Campaigns/CampaignList/CampaignListStatus";
import CampaignListStatusIcon from "@/components/Campaigns/CampaignList/CampaignListStatusIcon";
import CampaignListStats from "@/components/Campaigns/CampaignList/CampaignListStats";
import CampaignListDate from "@/components/Campaigns/CampaignList/CampaignListDate";
import CreateCampaignLogic from "@/components/Campaigns/CreateCampaignLogic";

import { RemoveCampaign } from "@/graphql/mutations";

export default {
  name: "CampaignListItem",
  components: {
    CampaignListDate,
    CampaignListSubject,
    CampaignListStatus,
    CampaignListStatusIcon,
    CampaignListStats,
    CreateCampaignLogic,
  },
  props: {
    campaign: {
      type: Object,
      required: true,
    },
    reloading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      removingCampaign: false,
    };
  },
  computed: {
    dateSent() {
      return this.campaign?.stats?.dateSent;
    },
    reloadLabel() {
      return this.reloading ? "reloading status" : "reload status";
    },
    status() {
      return this.reloading ? "RELOADING" : this.campaign.status;
    },
  },
  methods: {
    onRemoveCampaign() {
      this.$modal.show("dialog", {
        title: "📛 Confirm Deletion 📛",
        text: "Are you sure you want to delete this campaign?",
        buttons: [
          {
            title: "Yes",
            class: "vue-dialog-button bg-danger hover:bg-brand",
            handler: () => {
              this.$modal.hide("dialog");
              this.removeCampaign();
            },
          },
          {
            title: "No",
            class: "vue-dialog-button bg-support-light hover:bg-support",
          },
        ],
      });
    },
    async removeCampaign() {
      try {
        this.removingCampaign = true;

        await this.$apollo.mutate({
          mutation: RemoveCampaign,
          variables: {
            input: {
              campaignId: this.campaign.campaignId,
            },
          },
          update: () => {
            this.$emit("remove-campaign");
          },
        });
      } catch (error) {
        this.removingCampaign = false;
      }
    },
  },
};
</script>

<style scoped>
.campaign-list-item {
  @apply flex flex-wrap items-center justify-center rounded-lg mb-6 py-6 px-4 bg-white border shadow;
  border-color: #f1f1f1;

  &.removing {
    @apply pointer-events-none opacity-50;
  }

  @screen sm {
    @apply p-8;
  }

  @screen md {
    @apply p-10 mb-10;
  }

  @screen lg {
    @apply px-8 justify-start;
  }
}

.campaign-details {
  @apply flex flex-wrap items-center justify-center mb-4 w-full;

  @screen lg {
    @apply flex-1 justify-start flex-no-wrap mb-0;
  }
}

.campaign-meta {
  @apply flex justify-center;

  @screen lg {
    @apply justify-start;
  }
}

.campaign-actions {
  @apply flex flex-wrap items-center;

  @screen lg {
    @apply flex-no-wrap;
  }
}

.status-reload {
  @apply block p-0  mt-1 text-xs text-brand leading-none mx-auto;

  @screen lg {
    @apply m-0 mt-1;
  }
}

.send-button {
  @apply py-1 mb-1 inline-block;

  svg {
    @apply relative mr-1;
    top: 1px;
  }

  @screen lg {
    @apply ml-2 mb-0;
  }
}

.send-button-wrap {
  @apply text-center;

  @screen lg {
    @apply text-right w-auto;
    order: 1;
  }
}

.meta-button {
  @apply text-grey-dark font-lato py-1 px-2;

  @screen md {
    @apply text-sm;

    svg {
      @apply relative;
      top: 1px;
    }
  }
}

.meta-buttons-wrap {
  @apply flex mx-auto;

  @screen lg {
    @apply m-0;
  }
}

.delete-button {
  @apply text-danger opacity-50;

  &:hover {
    @apply opacity-100;
  }
}

.resend-campaign {
  @apply text-xs text-grey-darker m-0 text-brand mx-auto block;

  @screen lg {
    @apply mx-0;
  }

  &:hover {
    @apply no-underline text-brand-alt;

    .resend-campaign-help {
      @apply text-brand-alt;
    }
  }
}

.resend-campaign-help {
  @apply italic font-light text-grey-darker;
}
</style>
